import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { XCircle, Star, Calendar, MessageSquare } from 'lucide-react'
import { supabase } from '../services/supabase'

interface ContactSubmission {
  id: number
  name: string
  email: string
  message_type: string
  subject: string
  message: string
  rating: number
  is_public: boolean
  status: string
  created_at: string
  updated_at: string
}

const ContactAdminPage: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')

  useEffect(() => {
    fetchSubmissions()
  }, [])

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) {
        console.error('Database error:', error)
      } else {
        setSubmissions(data || [])
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateSubmissionStatus = async (id: number, status: string, isPublic: boolean) => {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .update({
          status: status || 'read',
          is_public: isPublic
        })
        .eq('id', id)
        .select()

      if (error) {
        console.error('Database error:', error)
      } else if (data && data[0]) {
        setSubmissions(prev => 
          prev.map(sub => 
            sub.id === id 
              ? { ...sub, status, is_public: isPublic }
              : sub
          )
        )
      }
    } catch (error) {
      console.error('Failed to update submission:', error)
    }
  }

  const filteredSubmissions = submissions.filter(sub => 
    filterStatus === 'all' || sub.status === filterStatus
  )

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'read': return 'bg-blue-100 text-blue-800'
      case 'replied': return 'bg-green-100 text-green-800'
      case 'resolved': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <>
      <Helmet>
        <title>Contact Admin - AI Decision Maker</title>
        <meta name="description" content="Admin panel for managing contact form submissions" />
      </Helmet>

      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contact Form Submissions</h1>
            <p className="text-gray-600 mt-2">Manage user feedback, suggestions, and support requests</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="resolved">Resolved</option>
            </select>
            <button
              onClick={fetchSubmissions}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
            >
              Refresh
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Submissions List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Submissions ({filteredSubmissions.length})
                  </h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {filteredSubmissions.map((submission) => (
                    <div
                      key={submission.id}
                      className={`p-6 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedSubmission?.id === submission.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setSelectedSubmission(submission)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-medium text-gray-900">{submission.name}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(submission.status)}`}>
                              {submission.status}
                            </span>
                            {submission.is_public && (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                Public
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{submission.email}</p>
                          {submission.subject && (
                            <p className="text-sm font-medium text-gray-900 mb-1">{submission.subject}</p>
                          )}
                          <p className="text-sm text-gray-600 line-clamp-2">{submission.message}</p>
                          <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                            <span className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(submission.created_at)}
                            </span>
                            {submission.rating > 0 && (
                              <span className="flex items-center">
                                <Star className="w-3 h-3 mr-1 fill-current text-yellow-400" />
                                {submission.rating}/5
                              </span>
                            )}
                            <span className="capitalize">{submission.message_type}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Submission Detail */}
            <div className="lg:col-span-1">
              {selectedSubmission ? (
                <div className="bg-white rounded-lg shadow p-6 sticky top-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Submission Details</h3>
                    <button
                      onClick={() => setSelectedSubmission(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <p className="text-gray-900">{selectedSubmission.name}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <p className="text-gray-900">{selectedSubmission.email}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <p className="text-gray-900 capitalize">{selectedSubmission.message_type}</p>
                    </div>

                    {selectedSubmission.subject && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                        <p className="text-gray-900">{selectedSubmission.subject}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                      <p className="text-gray-900 text-sm leading-relaxed">{selectedSubmission.message}</p>
                    </div>

                    {selectedSubmission.rating > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                        <div className="flex items-center">
                          {Array.from({ length: selectedSubmission.rating }).map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                          <span className="ml-2 text-sm text-gray-600">{selectedSubmission.rating}/5</span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={selectedSubmission.status}
                        onChange={(e) => updateSubmissionStatus(selectedSubmission.id, e.target.value, selectedSubmission.is_public)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isPublic"
                        checked={selectedSubmission.is_public}
                        onChange={(e) => updateSubmissionStatus(selectedSubmission.id, selectedSubmission.status, e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                        Show as public testimonial
                      </label>
                    </div>

                    <div className="pt-4 border-t">
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>Created: {formatDate(selectedSubmission.created_at)}</p>
                        <p>Updated: {formatDate(selectedSubmission.updated_at)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a submission to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ContactAdminPage 